import { useCallback, useMemo, useState, useEffect, memo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Loader2, AlertCircle } from 'lucide-react';
import { apiClient } from '@/api/client';
import { useWorkspaceStore } from '@/stores/workspace-store';
import type { ERDTab, SchemaMetadata, ColumnMeta } from '@/types';

// ═══════════════════════════════════════════
// Table Node Component
// ═══════════════════════════════════════════

interface TableNodeData {
  name: string;
  schema: string;
  columns: ColumnMeta[];
  connectionId: string;
  [key: string]: unknown;
}

const MAX_VISIBLE_COLUMNS = 15;

const TableNode = memo(function TableNode({ data }: NodeProps<Node<TableNodeData>>) {
  const cols = data.columns ?? [];
  const displayCols = cols.slice(0, MAX_VISIBLE_COLUMNS);
  const remaining = cols.length - MAX_VISIBLE_COLUMNS;

  return (
    <div
      className="bg-dbgo-panel border border-[var(--border-default)] rounded-lg shadow-lg overflow-hidden"
      style={{ minWidth: 200, maxWidth: 280 }}
    >
      <Handle type="target" position={Position.Left} className="!w-2 !h-2 !bg-accent-blue !border-0" />
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-accent-blue !border-0" />

      <div className="bg-accent-blue px-3 py-1.5 text-xs font-semibold text-white truncate">
        {data.schema}.{data.name}
      </div>

      <div className="divide-y divide-[var(--border-subtle)]">
        {displayCols.map((col) => (
          <div
            key={col.name}
            className="flex items-center gap-1.5 px-2 py-0.5 text-[11px]"
          >
            {col.isPrimaryKey && (
              <span className="text-accent-yellow flex-shrink-0 w-[14px] text-center">PK</span>
            )}
            {col.isForeignKey && !col.isPrimaryKey && (
              <span className="text-accent-blue flex-shrink-0 w-[14px] text-center">FK</span>
            )}
            {!col.isPrimaryKey && !col.isForeignKey && (
              <span className="w-[14px] flex-shrink-0" />
            )}
            <span className="text-text-primary flex-1 truncate">{col.name}</span>
            <span className="text-text-muted text-[10px] flex-shrink-0">{col.type}</span>
          </div>
        ))}
        {remaining > 0 && (
          <div className="px-2 py-0.5 text-[10px] text-text-muted italic text-center">
            ... and {remaining} more
          </div>
        )}
      </div>
    </div>
  );
});

const nodeTypes = { tableNode: TableNode };

// ═══════════════════════════════════════════
// Schema to React Flow transform
// ═══════════════════════════════════════════

function schemaToFlow(
  schema: SchemaMetadata,
  connectionId: string,
): { nodes: Node<TableNodeData>[]; edges: Edge[] } {
  const nodes: Node<TableNodeData>[] = [];
  const edges: Edge[] = [];

  const COLS = 4;
  const H_GAP = 300;
  const V_GAP = 40;
  const ROW_HEIGHT = 30; // header height base

  let col = 0;
  let rowYOffsets: number[] = [0, 0, 0, 0]; // Track y offset per column

  for (const db of schema.databases) {
    for (const s of db.schemas) {
      for (const table of s.tables) {
        const columns = table.columns ?? [];
        const visibleCols = Math.min(columns.length, MAX_VISIBLE_COLUMNS);
        const nodeHeight = visibleCols * 20 + ROW_HEIGHT + (columns.length > MAX_VISIBLE_COLUMNS ? 20 : 0);

        // Place in column with lowest y offset (simple bin-packing)
        let minCol = 0;
        let minY = rowYOffsets[0];
        for (let c = 1; c < COLS; c++) {
          if (rowYOffsets[c] < minY) {
            minY = rowYOffsets[c];
            minCol = c;
          }
        }
        col = minCol;

        nodes.push({
          id: `${s.name}.${table.name}`,
          type: 'tableNode',
          position: { x: col * H_GAP, y: rowYOffsets[col] },
          data: {
            name: table.name,
            schema: s.name,
            columns,
            connectionId,
          },
        });

        rowYOffsets[col] += nodeHeight + V_GAP;

        // Create edges for foreign keys
        for (const column of columns) {
          if (column.isForeignKey && column.referencesTable) {
            edges.push({
              id: `fk-${s.name}.${table.name}.${column.name}`,
              source: `${s.name}.${table.name}`,
              target: `${s.name}.${column.referencesTable}`,
              type: 'smoothstep',
              label: column.name,
              animated: false,
              style: { stroke: 'var(--text-muted)', strokeWidth: 1 },
              labelStyle: { fontSize: 10, fill: 'var(--text-muted)' },
            });
          }
        }
      }
    }
  }

  return { nodes, edges };
}

// ═══════════════════════════════════════════
// Schema Filter (multi-schema support)
// ═══════════════════════════════════════════

function getSchemaNames(schema: SchemaMetadata): string[] {
  const names: string[] = [];
  for (const db of schema.databases) {
    for (const s of db.schemas) {
      names.push(s.name);
    }
  }
  return names;
}

function filterSchema(schema: SchemaMetadata, selectedSchema: string | null): SchemaMetadata {
  if (!selectedSchema) return schema;
  return {
    databases: schema.databases.map((db) => ({
      ...db,
      schemas: db.schemas.filter((s) => s.name === selectedSchema),
    })),
  };
}

// ═══════════════════════════════════════════
// Inner ERD (needs ReactFlowProvider wrapper)
// ═══════════════════════════════════════════

function ERDInner({
  schema,
  connectionId,
  selectedSchema,
}: {
  schema: SchemaMetadata;
  connectionId: string;
  selectedSchema: string | null;
}) {
  const openTab = useWorkspaceStore((s) => s.openTab);

  const filteredSchema = useMemo(
    () => filterSchema(schema, selectedSchema),
    [schema, selectedSchema],
  );

  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => schemaToFlow(filteredSchema, connectionId),
    [filteredSchema, connectionId],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes/edges when schema data changes (initial load or filter change)
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const handleNodeDoubleClick = useCallback(
    (_event: React.MouseEvent, node: Node<TableNodeData>) => {
      const data = node.data;
      openTab({
        type: 'table',
        id: `table:${data.connectionId}:${data.schema}:${data.name}`,
        connectionId: data.connectionId,
        schema: data.schema,
        table: data.name,
        where: '',
        orderBy: '',
      });
    },
    [openTab],
  );

  return (
    <ReactFlow
      style={{ width: '100%', height: '100%' }}
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeDoubleClick={handleNodeDoubleClick}
      nodeTypes={nodeTypes}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      minZoom={0.1}
      maxZoom={2}
      proOptions={{ hideAttribution: true }}
    >
      <Background color="var(--border-subtle)" gap={20} size={1} />
      <Controls
        showInteractive={false}
        className="erd-controls"
      />
      <MiniMap
        nodeColor="var(--dbgo-panel)"
        maskColor="rgba(0, 0, 0, 0.3)"
        className="erd-minimap"
      />
    </ReactFlow>
  );
}

// ═══════════════════════════════════════════
// Main ERDView Component
// ═══════════════════════════════════════════

export function ERDView({ tab }: { tab: ERDTab }) {
  const { data: schema, isLoading, error } = useQuery<SchemaMetadata>({
    queryKey: ['schema', tab.connectionId],
    queryFn: () => apiClient.get<SchemaMetadata>(`/api/schema/${tab.connectionId}`),
    staleTime: 60_000,
  });

  const schemaNames = useMemo(() => (schema ? getSchemaNames(schema) : []), [schema]);
  const [selectedSchema, setSelectedSchema] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-dbgo-workarea">
        <Loader2 size={24} className="animate-spin text-text-muted" />
        <span className="ml-2 text-sm text-text-muted">Loading schema...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center bg-dbgo-workarea">
        <AlertCircle size={20} className="text-accent-red mr-2" />
        <span className="text-sm text-accent-red">
          {error instanceof Error ? error.message : 'Failed to load schema'}
        </span>
      </div>
    );
  }

  if (!schema?.databases?.length) {
    return (
      <div className="flex-1 flex items-center justify-center bg-dbgo-workarea">
        <span className="text-sm text-text-muted">No tables found</span>
      </div>
    );
  }

  // Count total tables
  let tableCount = 0;
  for (const db of schema.databases) {
    for (const s of db.schemas) {
      if (!selectedSchema || s.name === selectedSchema) {
        tableCount += s.tables.length;
      }
    }
  }

  return (
    <div className="h-full w-full flex flex-col bg-dbgo-workarea" style={{ overflow: 'hidden' }}>
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-3 py-1.5 border-b border-[var(--border-default)] bg-dbgo-panel flex-shrink-0">
        <span className="text-xs text-text-secondary font-medium">
          ERD — {tab.databaseName}
        </span>

        {schemaNames.length > 1 && (
          <select
            value={selectedSchema ?? ''}
            onChange={(e) => setSelectedSchema(e.target.value || null)}
            className="text-xs bg-dbgo-input border border-[var(--border-default)] rounded px-2 py-0.5 text-text-primary"
          >
            <option value="">All schemas</option>
            {schemaNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        )}

        <span className="text-[10px] text-text-muted">
          {tableCount} table{tableCount !== 1 ? 's' : ''}
        </span>

        <div className="flex-1" />

        <span className="text-[10px] text-text-muted">
          Double-click a table to open it
        </span>
      </div>

      {/* Diagram — React Flow needs explicit dimensions */}
      <div style={{ flex: '1 1 0%', position: 'relative', minHeight: 0, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' }}>
          <ReactFlowProvider key={`${tab.connectionId}-${selectedSchema ?? 'all'}`}>
            <ERDInner
              schema={schema}
              connectionId={tab.connectionId}
              selectedSchema={selectedSchema}
            />
          </ReactFlowProvider>
        </div>
      </div>
    </div>
  );
}
